import { mutateWithVariables, query, queryWithVariables } from '@/services/api/api';
import { ImageFileData, MeshTreeData } from '@/types/ui/MeshTreeData';
import { BasePrices } from '@/types/api/Model/Product';
import { SasDeletePayload } from '@/types/interfaces/sasDeleteLink';
import { MinimalPackageDto } from '@/types/api/Product/MinimalPackageDto';
/* eslint-disable @typescript-eslint/no-explicit-any*/

export const GetAllDistinctProductTypes = async (): Promise<any> => {
  const result = await queryWithVariables(
    `
        query {
          allDistinctProductTypes {
            productTypeId
            name,
            productTypeAttributes {
              productTypeAttributeId,
              productTypeAttributeName,
              productTypeId
            }
          }
        }
      `,
    {}
  );
  return result.data;
};

export const GetProductById = async (
  id: number
): Promise<{
  name: string;
  basePrices: BasePrices[];
} | null> => {
  try {
    const result = await queryWithVariables(
      `
      query ($id: Long!) {
        productById (id: $id){
          name
          basePrices{
            price
            country
          },
          productTypeAttribute {
            productTypeAttributeId,
            productTypeAttributeName,
            productTypeId
          }
        }
      }
    `,
      {
        id: id,
      }
    );
    return result.data.productById;
  } catch (error) {
    console.error('error', error);
    return null;
  }
};

export const GetProductTypeByName = async (name: any): Promise<any> => {
  try {
    const result = await queryWithVariables(
      `
      query ($name: String!) {
        productTypeByName (name: $name){
          name,
          productTypeAttributes {
            productTypeAttributeId,
            productTypeAttributeName,
            productTypeId
          }
        }
      }
    `,
      {
        name: [name],
      }
    );
    return result.data.productTypeByName[0].formFields;
  } catch (error) {
    console.error('error', error);
    return null;
  }
};

export const CreateProductType = async (productType: any): Promise<any> => {
  try {
    const result = await mutateWithVariables(
      `
      mutation ($productType: String!) {
        createProductType(productType: $productType)
      }
    `,
      {
        productType: productType,
      }
    );
    return result.data.createProductType;
  } catch (error) {
    console.error('error', error);
    return null;
  }
};

export const UpdateProductTypeByName = async (name: string, newName: string): Promise<any> => {
  try {
    const result = await mutateWithVariables(
      `
      mutation ($name: String!, $newName: String!) {
        updateProductTypeByName
        (
          name: $name,
          newName: $newName
        )
      }
    `,
      {
        name: name,
        newName: newName,
      }
    );
    return result.data.updateProductTypeByName;
  } catch (error) {
    console.error('error', error);
    return null;
  }
};

interface UpdateProductParams {
  productId: number | null;
  name: string;
  description: string | null;
  addressedDiseases?: [];
  sampleImagePaths?: [];
  defaultCharacteristics?: [];
  supportedGridTypes?: [];
  basePrices?: BasePrices[];
  packages?: [];
  type?: any;
  bodyPart?: any | null;
  thumbnail?: ImageFileData | null;
}

export const updateProduct = async ({
  productId,
  name,
  description,
  addressedDiseases = [],
  sampleImagePaths = [],
  defaultCharacteristics = [],
  basePrices = [],
  packages = [],
  type = {
    name: 'Testing Category',
    attributes: [],
  },
  bodyPart,
  supportedGridTypes = [],
  thumbnail,
}: UpdateProductParams): Promise<unknown> => {
  const mutationUpdateProduct = `
        mutation UpdateProduct($product: ProductInput!) {
            updateProduct(product: $product)
        }
    `;

  const data = {
    product: {
      addressedDiseases,
      basePrices,
      bodyPart,
      defaultCharacteristics,
      name,
      description,
      sampleImagePaths,
      supportedGridTypes,
      thumbnail: {
        content: thumbnail?.url,
        name: thumbnail?.name,
        type: thumbnail?.type,
        textures: [],
        thumbnailTextures: [],
      },
      packages,
      productId,
      type,
    },
  };

  const updateResult = await mutateWithVariables(mutationUpdateProduct, data);
  return updateResult;
};


export const getSasUrlForProductFileUpload = async (productId: number): Promise<SasPayload> => {
  const getSasUrlForProductFileUpload = await queryWithVariables(
    `
      query ProductFileUploadSas($productId: Long!) {
        sasUrlForProductFileUpload(productId: $productId) {
          sasUrl
          prefix
          expiresOn
          id
        }
      }
      `,
    {
      productId: productId,
    }
  );

  return getSasUrlForProductFileUpload.data;
}

export const getDeleteLinkForProductBlobUrl = async (blobUrl: string): Promise<SasDeletePayload> => {
  const getDeleteLinkForProductBlobUrl = await queryWithVariables(
    `
        query DeleteLinkForProductBlobUrl($blobUrl: String!) {
          deleteLinkForProductBlobUrl(blobUrl: $blobUrl) {
            sasUrl,
            expiresOn
          }
        }
      `,
    {
      blobUrl: blobUrl,
    }
  );

  return getDeleteLinkForProductBlobUrl.data.deleteLinkForProductBlobUrl;
};

export const DeleteProductById = async (productId: number): Promise<boolean> => {
  try {
    const result = await mutateWithVariables(
      `
      mutation ($productId: Long!) {
        deleteProduct(productId: $productId)
      }
    `,
      {
        productId: productId,
      }
    );
    return result.data.deleteProduct;
  } catch (error) {
    console.error('[Failed to delete product]', error);
    return false;
  }
};


export const getAllPackages = async (): Promise<MinimalPackageDto[]> => {
  const result = await query(
    `
      query getPackages {
        allPackages {
          packageId,
          description
        }
      }
    `
  );
  return result.data.allPackages;
};